import { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppProvider, Frame, Loading, Page, Navigation as PolarisNavigation, Box, InlineStack, Text, Button, ActionList, Popover } from '@shopify/polaris';
import { HomeIcon, ChartVerticalIcon, OrderIcon, ExitIcon, FilterIcon, CashDollarIcon, ShippingLabelIcon, ReceiptIcon, ConnectIcon, RefreshIcon, ClockIcon, SettingsIcon, ReceiptRefundIcon } from '@shopify/polaris-icons';
import enTranslations from '@shopify/polaris/locales/en.json';

// Import providers
import QueryProvider, { handleAuthChange, prefetchCommonData } from './providers/QueryProvider';
import ErrorBoundary, { APIErrorBoundary } from './components/ErrorBoundary';
import SessionManager from './components/SessionManager';
import { secureAuthUtils } from './utils/secureAuth';
import apiClient from './utils/api';

// Import pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import BrandConfiguration from './pages/BrandConfiguration';
import MarketplacePatterns from './pages/MarketplacePatterns';
// Import new analytics pages
import Analytics from './pages/Analytics';
import OrderManagement from './pages/OrderManagement';
import CommissionAdjustments from './pages/CommissionAdjustments';
import Invoices from './pages/Invoices';
import AccessRequests from './pages/AccessRequests';
import { useBrands, usePendingAccessRequestCount } from './utils/queries';
import { approvedBrandsOnly } from './utils/sidebar-brands';
import { countBadge } from './utils/sidebar-badge';
import { ShippingAndReturn } from './pages/ShippingAndReturn';
import Connector from './pages/Connector';
import Tracking from './pages/Tracking';
import InventorySync from './pages/InventorySync';
import SyncSettings from './pages/SyncSettings';
import Settings from './pages/Settings';
import BackgroundQueue from './pages/BackgroundQueue';
import BackgroundSyncHistory from './pages/BackgroundSyncHistory';
import RefundReasons from './pages/RefundReasons';

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);
  const [userMenuActive, setUserMenuActive] = useState(false);
  const location = useLocation();

  // Fetch brands for sidebar navigation (only when authenticated)
  const { data: brandsResponse, isLoading: brandsLoading, error: brandsError } = useBrands({}, {
    enabled: isAuthenticated
  });

  const brands = brandsResponse?.brands || brandsResponse || [];

  // The sidebar lists brands an admin can actually work on. See sidebar-brands.js for why a
  // pending brand is not one of them, and why this is not done on the request.
  const approvedBrands = useMemo(() => approvedBrandsOnly(brands), [brands]);

  // Badge on Access Requests. Not gated on anything but authentication: a request can arrive
  // while the admin is on any screen, because it is a merchant installing the app rather than
  // anything happening in here.
  const { data: pendingAccessRequestData } = usePendingAccessRequestCount({
    enabled: isAuthenticated,
  });
  const accessRequestBadge = countBadge(pendingAccessRequestData?.count);

  // Log any brand loading errors in development
  useEffect(() => {
    if (brandsError && process.env.NODE_ENV === 'development') {
      console.error('Brands loading error:', brandsError);
    }
  }, [brandsError]);

  useEffect(() => {
    // Initialize authentication from secure storage
    const initializeAuth = async () => {
      try {
        const token = await secureAuthUtils.getToken();
        const userData = await secureAuthUtils.getUserData();

        if (token && userData && secureAuthUtils.isSessionValid()) {
          // Set token in apiClient memory BEFORE triggering authenticated queries
          apiClient.token = token;
          // Update activity so periodic validation doesn't immediately invalidate
          secureAuthUtils.updateActivity();
          setIsAuthenticated(true);
          setUser(userData);
          // Prefetch brands data so sidebar loads immediately
          prefetchCommonData().catch(() => {});
        } else if (token || userData) {
          // Clear invalid session
          secureAuthUtils.clearSession();
        }
      } catch (error) {
        console.error('Failed to initialize authentication:', error);
        secureAuthUtils.clearSession();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const handleLogin = async (token, userData) => {
    // Use secure storage for authentication data
    await secureAuthUtils.setToken(token, userData);

    // Set token directly in apiClient memory to prevent race condition
    // This ensures the token is available before any API calls are triggered
    apiClient.token = token;

    setIsAuthenticated(true);
    setUser(userData);

    // Handle query cache for authenticated user
    handleAuthChange(true);

    // Prefetch common data for better UX
    try {
      await prefetchCommonData();
    } catch (error) {
      console.warn('Failed to prefetch data:', error);
    }
  };

  const handleLogout = (reason = 'user_requested') => {
    // Clear secure authentication data
    secureAuthUtils.clearSession();
    setIsAuthenticated(false);
    setUser(null);

    // Clear all query caches
    handleAuthChange(false);

    // Force a page reload to ensure clean state - use absolute path for security
    window.location.href = `${window.location.origin}/login`;
  };

  const toggleMobileNavigation = () => {
    setMobileNavigationActive(!mobileNavigationActive);
  };

  if (loading) {
    return (
      <Frame>
        <Loading />
      </Frame>
    );
  }

  if (!isAuthenticated) {
    return (
      <Page>
        <Login onLogin={handleLogin} />
      </Page>
    );
  }

  // Custom header to replace deprecated TopBar
  const headerMarkup = (
    <Box
      background="bg-surface"
      paddingBlock="300"
      paddingInline="400"
      borderBlockEndWidth="025"
      borderColor="border"
    >
      <InlineStack align="space-between" blockAlign="center">
        <InlineStack gap="400" align="start">
          <Button
            variant="tertiary"
            onClick={toggleMobileNavigation}
            icon={HomeIcon}
            accessibilityLabel="Toggle navigation"
          />
          <Text variant="headingMd" fontWeight="semibold">
            2W Console
          </Text>
        </InlineStack>

        <Popover
          active={userMenuActive}
          activator={
            <Button
              variant="tertiary"
              onClick={() => setUserMenuActive(!userMenuActive)}
              disclosure="down"
            >
              {/* No fabricated identity. 'Admin User' / 'Administrator' were literals that
                  rendered as though they were the signed-in account. The real fields come from
                  POST /auth/login, which returns { id, email, name, role }
                  (auth.controller.js:77-84) and is stored by secureAuthUtils.
                  NOTE: apiClient.getCurrentUser() targets GET /auth/profile, which does NOT
                  exist in the backend (the only auth route registered is /auth/login), so it is
                  deliberately not called here - it would 404. 'Account' below is a neutral UI
                  label for the menu trigger, not a claim about who is signed in. */}
              {user?.name || user?.email || 'Account'}
            </Button>
          }
          onClose={() => setUserMenuActive(false)}
        >
          <ActionList
            items={[
              // Only show the identity row when we actually have an identity.
              ...(user?.email || user?.role
                ? [{
                    content: [user?.email, user?.role].filter(Boolean).join(' · '),
                    disabled: true,
                  }]
                : []),
              {
                content: 'Logout',
                icon: ExitIcon,
                onAction: () => {
                  setUserMenuActive(false);
                  handleLogout();
                },
              },
            ]}
          />
        </Popover>
      </InlineStack>
    </Box>
  );

  // Sidebar navigation matching exact specification
  const navigationMarkup = (
    <PolarisNavigation location={location.pathname}>
      <PolarisNavigation.Section
        title="2W Overview"
        items={[
          {
            label: 'Dashboard',
            icon: HomeIcon,
            url: '/',
            exactMatch: true,
            selected: location.pathname === '/',
          },
          {
            label: 'Access Requests',
            icon: FilterIcon,
            url: '/access-requests',
            selected: location.pathname === '/access-requests',
            // How many brands are waiting to be let in. Nothing else in the console says so:
            // a request sat unseen until someone opened this page for an unrelated reason.
            //
            // Spread rather than set: countBadge returns undefined for a zero, a missing
            // count or a failed request, and `badge: undefined` is not the same to Polaris as
            // no badge key at all. See sidebar-badge.js for why each of those shows nothing.
            ...(accessRequestBadge ? { badge: accessRequestBadge } : {}),
          },
          {
            label: 'Analytics',
            icon: ChartVerticalIcon,
            url: '/analytics',
            selected: location.pathname === '/analytics',
          },
          {
            label: 'Orders Management',
            icon: OrderIcon,
            url: '/orders',
            selected: location.pathname === '/orders',
          },
          {
            label: 'Marketplace Settings',
            icon: FilterIcon,
            url: '/marketplace',
            selected: location.pathname === '/marketplace' || location.pathname.startsWith('/marketplace/'),
          },
          {
            label: 'Commission Adjustments',
            icon: CashDollarIcon,
            url: '/commission-adjustments',
            selected: location.pathname === '/commission-adjustments',
          },
          {
            label: 'Shipping & Return',
            icon: ShippingLabelIcon,
            url: '/shipping-and-return',
            selected: location.pathname === '/shipping-and-return',
          },
          {
            label: 'Invoices',
            icon: ReceiptIcon,
            url: '/invoices',
            selected: location.pathname === '/invoices',
          },
          {
            // Application-wide settings, which is why it closes this section rather than
            // sitting under Connector beside Sync Settings — nothing on it is the
            // connector's, and the next notice to need a recipient will not be either.
            label: 'Settings',
            icon: SettingsIcon,
            url: '/settings',
            selected: location.pathname === '/settings',
          },
        ]}
      />

      <PolarisNavigation.Section
        title="Connector"
        items={[
          {
            label: 'Retailer Accounts',
            icon: ConnectIcon,
            url: '/connector',
            selected: location.pathname === '/connector',
          },
          {
            // Beneath Retailer Accounts because a refund reason code belongs to ONE retailer
            // account and means nothing without it: the list is per account, and the scan
            // reads the orders already stored against that account. It is not under Shipping
            // & Return, even though what it decides is whether a return fee is charged —
            // that screen sets the fee for every brand, while this one says which refunds
            // are a return at all, retailer by retailer, and is maintained by whoever is
            // looking after the connector.
            label: 'Refund Reasons',
            icon: ReceiptRefundIcon,
            url: '/refund-reasons',
            selected: location.pathname === '/refund-reasons',
          },
          {
            label: 'Inventory Sync',
            icon: ConnectIcon,
            url: '/inventory-sync',
            selected: location.pathname === '/inventory-sync',
          },
          {
            label: 'Tracking',
            icon: ConnectIcon,
            url: '/tracking',
            selected: location.pathname === '/tracking',
          },
          {
            label: 'Sync Settings',
            icon: ConnectIcon,
            url: '/sync-settings',
            selected: location.pathname === '/sync-settings',
          },
        ]}
      />

      {/* Background Jobs.

          Both pages are GLOBAL — every job on every retailer account, no brand and no account
          filter applied. They were the last two cards on Retailer Accounts, below the accounts
          list, the SKU map and the global orders table, so "is anything stuck?" was the
          longest scroll in the console to an answer that is not about accounts at all. They
          sit together because they are read together: the queue is live and ages out after two
          hours, the history is the permanent record. */}
      <PolarisNavigation.Section
        title="Background Jobs"
        items={[
          {
            label: 'Queue',
            icon: RefreshIcon,
            url: '/queue',
            selected: location.pathname === '/queue',
          },
          {
            label: 'Sync History',
            icon: ClockIcon,
            url: '/sync-history',
            selected: location.pathname === '/sync-history',
          },
        ]}
      />

      {/* Brands Section - Collapsible to prevent clutter */}
      {brandsLoading && (
        <PolarisNavigation.Section
          title="Brands"
          items={[
            {
              label: 'Loading brands...',
              disabled: true
            }
          ]}
        />
      )}
      {/* Keyed on the APPROVED list, not on `brands`. Testing the unfiltered list would render
          a "Brands" heading with nothing under it for an installation whose only brands are
          still pending. */}
      {!brandsLoading && approvedBrands.length > 0 && (
        <PolarisNavigation.Section
          title="Brands"
          items={approvedBrands.map((brand) => ({
            label: brand.name,
            url: `/brands/${brand.id}`,
            selected: location.pathname === `/brands/${brand.id}`,
          }))}
        />
      )}
      {!brandsLoading && brandsError && (
        <PolarisNavigation.Section
          title="Brands"
          items={[
            {
              label: 'Error loading brands',
              disabled: true
            }
          ]}
        />
      )}
    </PolarisNavigation>
  );

  return (
    <Frame
      topBar={headerMarkup}
      navigation={navigationMarkup}
      showMobileNavigation={mobileNavigationActive}
      onNavigationDismiss={toggleMobileNavigation}
    >
      {/* onSessionExtended is omitted rather than passed as `() => {}`. SessionManager guards the
          call (`if (onSessionExtended)`), and the actual extension is already done inside it by
          secureAuthUtils.updateActivity() - there is nothing for the parent to do. It is NOT
          wired to apiClient.refreshToken(): that posts to /auth/refresh, which does not exist in
          the backend, and its own catch clears the session and dispatches auth_failure - so
          "Extend Session" would log the admin straight out. */}
      <SessionManager onLogout={handleLogout} />
      <ErrorBoundary
        title="Page Loading Error"
        fallbackComponent="AppRoutes"
        level="error"
      >
        <Routes>
          <Route path="/" element={
            <APIErrorBoundary>
              <Dashboard />
            </APIErrorBoundary>
          } />
          <Route path="/analytics" element={
            <APIErrorBoundary>
              <Analytics />
            </APIErrorBoundary>
          } />
          <Route path="/access-requests" element={
            <APIErrorBoundary>
              <AccessRequests />
            </APIErrorBoundary>
          } />
          <Route path="/brands" element={<Navigate to="/" replace />} />
          <Route path="/brands/:brandId" element={
            <APIErrorBoundary>
              <BrandConfiguration />
            </APIErrorBoundary>
          } />
          <Route path="/marketplace" element={
            <APIErrorBoundary>
              <MarketplacePatterns />
            </APIErrorBoundary>
          } />
          <Route path="/orders" element={
            <APIErrorBoundary>
              <OrderManagement />
            </APIErrorBoundary>
          } />
          <Route path="/commission-adjustments" element={
            <APIErrorBoundary>
              <CommissionAdjustments />
            </APIErrorBoundary>
          } />
          <Route path="/shipping-and-return" element={
            <APIErrorBoundary>
              <ShippingAndReturn />
            </APIErrorBoundary>
          } />
          <Route path="/invoices" element={
            <APIErrorBoundary>
              <Invoices />
            </APIErrorBoundary>
          } />
          <Route path="/settings" element={
            <APIErrorBoundary>
              <Settings />
            </APIErrorBoundary>
          } />
          <Route path="/tracking" element={
            <APIErrorBoundary>
              <Tracking />
            </APIErrorBoundary>
          } />
          <Route path="/inventory-sync" element={
            <APIErrorBoundary>
              <InventorySync />
            </APIErrorBoundary>
          } />
          <Route path="/sync-settings" element={
            <APIErrorBoundary>
              <SyncSettings />
            </APIErrorBoundary>
          } />
          <Route path="/connector" element={
            <APIErrorBoundary>
              <Connector />
            </APIErrorBoundary>
          } />
          <Route path="/refund-reasons" element={
            <APIErrorBoundary>
              <RefundReasons />
            </APIErrorBoundary>
          } />
          <Route path="/queue" element={
            <APIErrorBoundary>
              <BackgroundQueue />
            </APIErrorBoundary>
          } />
          <Route path="/sync-history" element={
            <APIErrorBoundary>
              <BackgroundSyncHistory />
            </APIErrorBoundary>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
    </Frame>
  );
}

function App() {
  return (
    <QueryProvider>
      <AppProvider
        i18n={enTranslations}
      >
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppContent />
        </Router>
      </AppProvider>
    </QueryProvider>
  );
}

export default App;